import React from "react";
import { Stack, TextInput, Grid } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface CountupBlockSettingsData {
  startDateTime: string; // UTC ISO string
  headerText?: string;
  footerText?: string;
}

export interface CountupBlockSettingsProps {
  /** Current settings data */
  value: CountupBlockSettingsData | null;

  /** Callback when settings change */
  onChange: (settings: CountupBlockSettingsData) => void;

  /** Validation errors for specific fields */
  errors?: Partial<Record<keyof CountupBlockSettingsData, string>>;
}

export const CountupBlockSettings: React.FC<CountupBlockSettingsProps> = ({
  value,
  onChange,
  errors = {},
}) => {
  // Initialize default values if not provided
  const settings: CountupBlockSettingsData = value || {
    startDateTime: dayjs().toISOString(),
    headerText: "",
    footerText: "",
  };

  const handleChange = (
    field: keyof CountupBlockSettingsData,
    newValue: string | Date | null
  ) => {
    const updatedSettings = { ...settings };

    if (
      field === "startDateTime" &&
      (newValue instanceof Date || typeof newValue === "string")
    ) {
      // Convert local date/time to UTC ISO string
      updatedSettings.startDateTime = dayjs(newValue)
        .utc()
        .format("YYYY-MM-DDTHH:mm:00.000Z");
    } else {
      updatedSettings[field] = newValue as any;
    }

    onChange(updatedSettings);
  };

  // Convert UTC string back to local Date for the picker
  const getLocalDateValue = (): Date | null => {
    if (!settings.startDateTime) return null;
    return dayjs(settings.startDateTime).local().toDate();
  };

  return (
    <Stack gap="sm">
      <Grid>
        <Grid.Col span={12}>
          <DateTimePicker
            label="Start Date & Time"
            placeholder="Select count-up start date and time"
            value={getLocalDateValue()}
            onChange={(date) => handleChange("startDateTime", date)}
            error={errors.startDateTime}
            required
            withAsterisk
            clearable={false}
            valueFormat="YYYY-MM-DD hh:mm A"
            timePickerProps={{
              withDropdown: true,
              format: "12h",
            }}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <TextInput
            label="Header Text"
            placeholder="Enter header text (optional)"
            value={settings.headerText || ""}
            onChange={(e) => handleChange("headerText", e.target.value)}
            error={errors.headerText}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <TextInput
            label="Footer Text"
            placeholder="Enter footer text (optional)"
            value={settings.footerText || ""}
            onChange={(e) => handleChange("footerText", e.target.value)}
            error={errors.footerText}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
